// Copyright (C) 2024 Nethesis S.r.l.
// SPDX-License-Identifier: AGPL-3.0-or-later

import React, { type ReactNode, FC, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Dispatch, RootState } from '../store'
import { io } from 'socket.io-client'
import { getApiMode } from './RestAPI'
import { getDisplayName } from '../lib/phone/conversation'
import { searchPhonebook } from '../services/phonebook'
import { getCurrentUserInfo } from '../services/user'
import busyRingtone from '../static/busy_ringtone'
import {
  dispatchMainPresence,
  dispatchConversations,
  dispatchQueueUpdate,
  dispatchQueueMemberUpdate,
  dispatchCurrentUserQueueCallWaiting,
  dispatchCurrentUserQueueCallConnected,
  dispatchAlreadyLogin,
  dispatchServerReload,
  dispatchParkingUpdate,
  dispatchExtensions,
  dispatchUrlCall,
  dispatchDefaultDeviceUpdate,
  dispatchVideoCallStarted,
} from '../events'
import { store } from '../store'
import { dispatchSummaryReady, eventDispatch, useEventListener, withTimeout } from '../utils'
import type {
  ConversationTypes,
  ExtensionTypes,
  QueuesUpdateTypes,
  QueueUpdateMemberTypes,
  MainPresenceTypes,
  CurrentUserQueueCallEventTypes,
} from '../types'
import { getTimestampInSeconds } from '../utils/genericFunctions/timestamp'
import { userTotallyFree } from '../lib/user/extensions'
import { isEmpty } from '../utils/genericFunctions/isEmpty'
import { isPhysical } from '../lib/user/default_device'
import { ScreenSharingMessage, VideoCallMessage } from './VideoView'
import { checkMediaPermissions } from '../lib/devices/devices'
import { isFromStreaming } from '../utils/streaming/isFromStreaming'
import { getStreamingSourceId } from '../utils/streaming/getStreamingSourceId'
import { subscribe } from '../services/user'
import { isFromTrunk } from '../lib/user/extensions'
import { getCurrentCallQueueContext } from '../lib/phone/queue'

interface SocketProps {
  children: ReactNode
  hostName: string
  username: string
  authToken: string
  reload: boolean
  reloadedCallback: () => void
  uaType: string
}

interface SummaryConversationCacheEntry {
  conversation: ConversationTypes
  cachedAt: number
}

const SUMMARY_CONVERSATION_CACHE_TTL_MS = 10 * 60 * 1000

export const Socket: FC<SocketProps> = ({
  hostName,
  username,
  authToken,
  reload,
  reloadedCallback,
  children,
  uaType,
}) => {
  const dispatch = useDispatch<Dispatch>()
  const connectionCheckInterval = useRef<any>()
  const socket = useRef<any>()
  const isUpdatingUserInfo = useRef(false)
  const consecutivePingTimeouts = useRef(0)
  const currentUserQueueCallPhase = useRef<{
    conversationId: string
    phase: 'waiting' | 'connected'
  } | null>(null)
  const summaryConversationCache = useRef<Map<string, SummaryConversationCacheEntry>>(new Map())
  // Caller names resolved via a fallback phonebook lookup (by digits-only
  // number), used when the server could not resolve the name itself (e.g.
  // group-shared phonebook contacts). Persisted across socket events so the
  // resolved name survives the ringing -> connected transitions.
  const resolvedContactNamesRef = useRef<Map<string, string>>(new Map())
  // Conversation ids for which a fallback lookup has already been issued, to
  // avoid firing the same request on every socket event.
  const contactLookupConversationsRef = useRef<Set<string>>(new Set())
  const STALE_CONNECTION_THRESHOLD = 3 // Force reconnect after 3 consecutive ping timeouts
  // Identifiers of the transcription the user currently has open. Incoming
  // realtime chunks are matched against these to avoid mixing transcripts from
  // a different concurrent call into the open view.
  const activeTranscriptionIdsRef = useRef<{ linkedid: string | null; uniqueid: string | null }>({
    linkedid: null,
    uniqueid: null,
  })

  // Event listener for starting transcription
  useEventListener('phone-island-start-transcription', (data: any) => {
    if (socket.current) {
      // Fall back to the other identifier so both fields are always populated
      // (the backend keys the stream on linkedid || uniqueid).
      const linkedid = data?.linkedid || data?.linkedId || data?.uniqueid || data?.uniqueId || null
      const uniqueid = data?.uniqueid || data?.uniqueId || linkedid
      if (!linkedid && !uniqueid) {
        return
      }
      activeTranscriptionIdsRef.current = { linkedid, uniqueid }
      socket.current.emit('start_transcription', {
        linkedid,
        uniqueid,
      })
    }
  })

  // Event listener for stopping transcription
  useEventListener('phone-island-stop-transcription', (data: any) => {
    if (socket.current) {
      // Fall back to the other identifier so both fields are always populated
      // (matches the start_transcription payload above).
      const linkedid = data?.linkedid || data?.linkedId || data?.uniqueid || data?.uniqueId || null
      const uniqueid = data?.uniqueid || data?.uniqueId || linkedid
      if (!linkedid && !uniqueid) {
        return
      }
      activeTranscriptionIdsRef.current = { linkedid: null, uniqueid: null }
      socket.current.emit('stop_transcription', {
        linkedid,
        uniqueid,
      })
    }
  })

  useEventListener(
    'phone-island-current-user-queue-call-waiting',
    (data: CurrentUserQueueCallEventTypes) => {
      dispatch.currentCall.updateCurrentCall({
        throughQueue: true,
        queueId: data?.queueId || '',
        queueName: data?.queueName || '',
        queueNumber: data?.queueNumber || '',
        queuePosition: data?.queuePosition || '',
        queueWaitingTime: data?.queueWaitingTime || 0,
      })
    },
  )

  useEventListener(
    'phone-island-current-user-queue-call-connected',
    (data: CurrentUserQueueCallEventTypes) => {
      dispatch.currentCall.updateCurrentCall({
        throughQueue: true,
        queueId: data?.queueId || '',
        queueName: data?.queueName || '',
        queueNumber: data?.queueNumber || '',
        queuePosition: data?.queuePosition || '',
        queueWaitingTime: data?.queueWaitingTime || 0,
      })
    },
  )

  const checkDefaultDeviceConversationActive = (conv: any) => {
    dispatch.currentCall.updateCurrentCall({
      conversationId: conv.id,
      accepted: true,
      incoming: conv.direction === 'in' ? false : undefined,
    })
    eventDispatch('phone-island-call-answered', {})

    // Stop the local audio element ringing
    store.dispatch.player.stopAudioPlayer()
    store.dispatch.player.setAudioPlayerLoop(false)
  }

  useEffect(() => {
    /**
     * Helper function to handle streaming source detection and subscription
     */
    const handleStreamingSource = (conv: ConversationTypes) => {
      // Check if the call is from a streaming source
      if (conv.counterpartNum && isFromStreaming(conv.counterpartNum)) {
        // Set isFromStreaming flag to true
        dispatch.island.setIsFromStreaming(true)

        // Store the streaming source number in the currentCall state for future reference
        dispatch.currentCall.updateCurrentCall({
          streamingSourceNumber: conv.counterpartNum,
        })

        // Find the source ID and subscribe to streaming updates
        const sourceId = getStreamingSourceId(conv.counterpartNum)
        if (sourceId) {
          // Subscribe to streaming updates
          subscribe({ id: sourceId }).catch((error) =>
            console.error('Error subscribing to streaming source:', error),
          )
        }
      }
    }

    const onlyDigits = (value?: string) => (value || '').replace(/\D/g, '')

    /**
     * Display name for a conversation, preferring a name resolved by the
     * fallback phonebook lookup when the server itself could not resolve one
     * (i.e. when getDisplayName falls back to the bare number). Group-shared
     * phonebook contacts are only resolved by the middleware search endpoint,
     * never by the realtime server side.
     */
    const getResolvedDisplayName = (conv: ConversationTypes) => {
      const serverName = getDisplayName(conv)
      const number = (conv?.counterpartNum || '').trim()
      // The server already resolved a real name (not the bare number).
      if (serverName && serverName !== number) {
        return serverName
      }
      const cached = number ? resolvedContactNamesRef.current.get(onlyDigits(number)) : undefined
      return cached || serverName
    }

    /**
     * Best-effort fallback: for an incoming external call whose name the server
     * did not resolve, query the middleware phonebook by number and update the
     * displayed caller name. Reuses the same search path as the phonebook UI,
     * so it honors the group-shared visibility rules.
     */
    const resolveIncomingContactName = (conv: ConversationTypes) => {
      if (!conv || conv.direction !== 'in') return
      const number = (conv.counterpartNum || '').trim()
      if (number.length < 3) return

      // Skip when the server already provided a real name.
      const serverName = (conv.counterpartName || '').trim()
      if (serverName && serverName !== '<unknown>' && serverName !== number) return

      // Skip internal extensions (operators resolve via the users store) and
      // streaming sources (handled separately).
      const { extensions } = store.getState().users
      if (extensions && extensions[number]) return
      if (isFromStreaming(number)) return

      // Already resolved or already looked up for this conversation.
      const target = onlyDigits(number)
      if (resolvedContactNamesRef.current.has(target)) return
      if (conv.id && contactLookupConversationsRef.current.has(conv.id)) return
      if (conv.id) {
        // Best-effort de-dup cache: bound it so a long-lived session cannot grow
        // it without limit. Clearing only risks one redundant lookup.
        if (contactLookupConversationsRef.current.size > 500) {
          contactLookupConversationsRef.current.clear()
        }
        contactLookupConversationsRef.current.add(conv.id)
      }

      searchPhonebook(1, number, '')
        .then((result: any) => {
          const rows = result?.rows || []
          const match = rows.find((row: any) =>
            [row.extension, row.workphone, row.homephone, row.cellphone, row.fax].some(
              (field: string) => {
                const digits = onlyDigits(field)
                return (
                  digits.length > 0 &&
                  (digits === target || digits.endsWith(target) || target.endsWith(digits))
                )
              },
            ),
          )
          const resolvedName = (match?.name || match?.company || '').trim()
          if (!resolvedName) return

          resolvedContactNamesRef.current.set(target, resolvedName)

          // Only apply if the same call is still the current one.
          const current = store.getState().currentCall
          if (onlyDigits(current.number) === target) {
            dispatch.currentCall.updateCurrentCall({ displayName: resolvedName })
          }
        })
        .catch(() => {
          // Fallback resolution is best-effort; ignore failures.
        })
    }

    const getCurrentCallQueuePayload = (conv: ConversationTypes) => {
      return getCurrentCallQueueContext(conv, store.getState().queue)
    }

    const getQueuePayloadWithFallback = (
      conv: ConversationTypes,
      queueContext: ReturnType<typeof getCurrentCallQueuePayload>,
    ) => {
      const currentCall = store.getState().currentCall
      const queueId = queueContext?.queueId || currentCall.queueId || ''
      const queueName = queueContext?.queueName || currentCall.queueName || ''
      const queueNumber = queueContext?.queueNumber || currentCall.queueNumber || ''

      return {
        conversationId: conv.id,
        linkedId: conv.linkedId,
        uniqueId: conv.uniqueId,
        ownerExtension: conv.owner,
        number: `${conv.counterpartNum || ''}`,
        queueId,
        queueName,
        queueNumber,
        queuePosition: queueContext?.queuePosition || '',
        queueWaitingTime: queueContext?.queueWaitingTime || 0,
      }
    }

    const syncCurrentUserQueueCall = (conv: ConversationTypes | null) => {
      if (!conv) {
        currentUserQueueCallPhase.current = null
        return
      }

      const queueContext = getCurrentCallQueuePayload(conv)
      const payload = getQueuePayloadWithFallback(conv, queueContext)
      const hasQueueContext = Boolean(payload.queueId || payload.queueName || payload.queueNumber)

      if (!hasQueueContext) {
        currentUserQueueCallPhase.current = null
        return
      }

      if (!conv.connected) {
        const shouldDispatchWaiting =
          currentUserQueueCallPhase.current?.conversationId !== conv.id ||
          currentUserQueueCallPhase.current?.phase !== 'waiting'

        if (shouldDispatchWaiting) {
          dispatchCurrentUserQueueCallWaiting(payload)
          currentUserQueueCallPhase.current = {
            conversationId: conv.id,
            phase: 'waiting',
          }
        }

        return
      }

      const shouldDispatchConnected =
        currentUserQueueCallPhase.current?.conversationId !== conv.id ||
        currentUserQueueCallPhase.current?.phase !== 'connected'

      if (shouldDispatchConnected) {
        dispatchCurrentUserQueueCallConnected(payload)
        currentUserQueueCallPhase.current = {
          conversationId: conv.id,
          phase: 'connected',
        }
      }
    }

    const getActiveConversationForCurrentCall = (): ConversationTypes | null => {
      const { conversations } = store.getState().currentUser
      const { conversationId } = store.getState().currentCall
      let selectedConversation: ConversationTypes | null = null

      Object.values(conversations || {}).forEach((extensionConversations) => {
        Object.values(extensionConversations || {}).forEach((conversation) => {
          if (selectedConversation?.id && selectedConversation.id === conversationId) {
            return
          }

          if (conversationId && conversation.id === conversationId) {
            selectedConversation = conversation
            return
          }

          if (!selectedConversation) {
            selectedConversation = conversation
            return
          }

          if (conversation.connected && !selectedConversation.connected) {
            selectedConversation = conversation
            return
          }

          if (
            conversation.connected === selectedConversation.connected &&
            conversation.startTime > selectedConversation.startTime
          ) {
            selectedConversation = conversation
          }
        })
      })

      return selectedConversation
    }

    const getConversationLinkedId = (conversation?: any) =>
      conversation?.linkedId || conversation?.linkedid || ''

    const getConversationUniqueId = (conversation?: any) =>
      conversation?.uniqueId || conversation?.uniqueid || ''

    const isSummaryConversationCandidate = (conversation?: any) =>
      !!conversation?.connected &&
      !!getConversationLinkedId(conversation) &&
      !!getConversationUniqueId(conversation)

    const selectLiveSummaryConversation = (extensionConversations?: Record<string, any>) => {
      let selectedConversation: any = null
      let selectedConversationKey: string | undefined
      const entries = Object.entries(extensionConversations || {}).filter(
        ([, conversation]) => conversation && Object.keys(conversation).length > 0,
      )

      entries.forEach(([key, currentConversation]) => {
        if (!selectedConversation) {
          selectedConversation = currentConversation
          selectedConversationKey = key
          return
        }

        const selectedIsSummaryCandidate = isSummaryConversationCandidate(selectedConversation)
        const currentIsSummaryCandidate = isSummaryConversationCandidate(currentConversation)

        if (currentIsSummaryCandidate && !selectedIsSummaryCandidate) {
          selectedConversation = currentConversation
          selectedConversationKey = key
          return
        }

        if (currentIsSummaryCandidate === selectedIsSummaryCandidate) {
          const selectedConnected = !!selectedConversation.connected
          const currentConnected = !!currentConversation.connected

          if (currentConnected && !selectedConnected) {
            selectedConversation = currentConversation
            selectedConversationKey = key
            return
          }

          const selectedStartTime = selectedConversation.startTime ?? 0
          const currentStartTime = currentConversation.startTime ?? 0

          if (currentConnected === selectedConnected && currentStartTime > selectedStartTime) {
            selectedConversation = currentConversation
            selectedConversationKey = key
          }
        }
      })

      return {
        conversation: selectedConversation,
        key: selectedConversationKey,
        count: entries.length,
      }
    }

    const getCachedSummaryConversation = (extension?: string) => {
      if (!extension) {
        return null
      }

      const cached = summaryConversationCache.current.get(extension)
      if (!cached) {
        return null
      }

      const cacheAgeMs = Date.now() - cached.cachedAt
      if (cacheAgeMs > SUMMARY_CONVERSATION_CACHE_TTL_MS) {
        summaryConversationCache.current.delete(extension)
        return null
      }

      return {
        ...cached,
        cacheAgeMs,
      }
    }

    const selectSummaryConversationForHangup = (extension?: string) => {
      const { conversations } = store.getState().currentUser
      const extensionConversations = extension ? conversations?.[extension] : undefined
      const liveSelection = selectLiveSummaryConversation(extensionConversations)

      if (isSummaryConversationCandidate(liveSelection.conversation)) {
        return {
          ...liveSelection,
          source: 'live',
          cacheAgeMs: undefined,
          hasConversationForCaller: !!extensionConversations,
          liveConversationKeys: Object.keys(extensionConversations || {}),
        }
      }

      const cached = getCachedSummaryConversation(extension)
      if (cached && isSummaryConversationCandidate(cached.conversation)) {
        return {
          conversation: cached.conversation,
          key: cached.conversation.id,
          count: liveSelection.count,
          source: 'cache',
          cacheAgeMs: cached.cacheAgeMs,
          hasConversationForCaller: !!extensionConversations,
          liveConversationKeys: Object.keys(extensionConversations || {}),
        }
      }

      return {
        ...liveSelection,
        source: liveSelection.conversation ? 'live_invalid' : 'none',
        cacheAgeMs: cached?.cacheAgeMs,
        hasConversationForCaller: !!extensionConversations,
        liveConversationKeys: Object.keys(extensionConversations || {}),
      }
    }

    const cacheSummaryConversations = (extension?: string, conversations?: Record<string, any>) => {
      if (!extension) {
        return
      }

      Object.values(conversations || {}).forEach((conversation) => {
        if (!isSummaryConversationCandidate(conversation)) {
          return
        }

        summaryConversationCache.current.set(extension, {
          conversation,
          cachedAt: Date.now(),
        })
      })
    }

    const shouldKeepCurrentAcceptedCall = (ownerExtension?: string) => {
      const { accepted, ownerExtension: currentOwnerExtension } = store.getState().currentCall

      return (
        accepted &&
        !!currentOwnerExtension &&
        !!ownerExtension &&
        currentOwnerExtension !== ownerExtension
      )
    }

    /**
     * Manages event and data for the currentUser
     *
     * @param res The data from the socket
     * @param conv The conversation data
     */
    const handleCurrentUserEvents = (res: ExtensionTypes, conv: ConversationTypes) => {
      // Handle transferring data
      const { transferring, transferSwitching, transferCalls } = store.getState().currentCall

      const view = store.getState().island.view
      // Check conversation isn't empty
      if (Object.keys(conv).length > 0) {
        // With conversation
        if (res.status) {
          const { extensions } = store.getState().users
          const { default_device } = store.getState().currentUser
          const { endpoints, username } = store.getState().currentUser
          const { incoming, outgoing } = store.getState().currentCall

          const hasOnlineNethlink = () => {
            if (!extensions || !username) return false

            // Get all extensions for current user
            const userExtensions: any = Object.values(extensions).filter(
              (ext) => ext?.username === username,
            )

            // Check if any extension is nethlink type and online
            return userExtensions?.some((ext) => {
              const endpointExtension = endpoints?.extension.find(
                (endpoint) => endpoint.id === ext?.exten,
              )
              return endpointExtension?.type === 'nethlink' && ext?.status !== 'offline'
            })
          }
          switch (res.status) {
            case 'ringing':
              if (shouldKeepCurrentAcceptedCall(conv.owner)) {
                break
              }

              const ringingQueueContext = getCurrentCallQueuePayload(conv)
              const ringingQueuePayload = getQueuePayloadWithFallback(conv, ringingQueueContext)

              syncCurrentUserQueueCall(conv)

              // Handle streaming source for incoming calls
              handleStreamingSource(conv)

              // Fallback: resolve the caller name from the phonebook when the
              // server could not (e.g. group-shared contacts).
              resolveIncomingContactName(conv)

              if (
                (uaType === 'mobile' && hasOnlineNethlink()) ||
                (uaType === 'desktop' &&
                  (default_device?.type === 'webrtc' ||
                    (default_device?.type === undefined && !hasOnlineNethlink()) ||
                    (!hasOnlineNethlink() && default_device?.type === 'physical')))
              ) {
                dispatch.currentCall.checkIncomingUpdatePlay({
                  conversationId: conv.id,
                  displayName: getResolvedDisplayName(conv),
                  number: `${conv.counterpartNum}`,
                  incomingSocket: true,
                  incoming: true,
                  throughQueue: Boolean(
                    ringingQueuePayload.queueId ||
                      ringingQueuePayload.queueName ||
                      ringingQueuePayload.queueNumber,
                  ),
                  queueId: ringingQueuePayload.queueId,
                  queueName: ringingQueuePayload.queueName,
                  queueNumber: ringingQueuePayload.queueNumber,
                  queuePosition: ringingQueuePayload.queuePosition,
                  queueWaitingTime: ringingQueuePayload.queueWaitingTime,
                  username:
                    `${
                      extensions &&
                      extensions[conv.counterpartNum] &&
                      extensions[conv.counterpartNum].username
                    }` || '',
                  ownerExtension: conv.owner,
                })
                store.dispatch.island.setIslandView('call')
              }
              // Get updated user info to refresh open url param type
              if (!isUpdatingUserInfo.current) {
                isUpdatingUserInfo.current = true
                getCurrentUserInfo()
                  .then((userInfo) => {
                    if (userInfo) {
                      dispatch.currentUser.updateCurrentUser(userInfo)
                      eventDispatch('phone-island-user-informations-update', { ...userInfo })
                      if (userInfo.settings && userInfo.settings.open_param_url) {
                        dispatch.paramUrl.setOpenParamUrlType(userInfo.settings.open_param_url)
                      } else {
                        dispatch.paramUrl.setOpenParamUrlType('never')
                      }
                    }
                  })
                  .catch((error) => {
                    console.error('Error getting current user info:', error)
                  })
                  .finally(() => {
                    setTimeout(() => {
                      isUpdatingUserInfo.current = false
                    }, 100)
                  })
              }
              const { openParamUrlType } = store.getState().paramUrl
              const { urlOpened } = store.getState().island
              if (openParamUrlType === 'ringing' && !urlOpened) {
                // Calculate throughTrunk based on counterpartNum
                const calculatedThroughTrunk = isFromTrunk(conv.counterpartNum)

                // Update throughTrunk in paramUrl store
                store.dispatch.paramUrl.setThroughTrunk(calculatedThroughTrunk)

                store.dispatch.island.setUrlOpened(false)
                eventDispatch('phone-island-url-parameter-opened', {
                  counterpartNum: conv.counterpartNum,
                  counterpartName: getResolvedDisplayName(conv),
                  owner: conv.owner,
                  uniqueId: conv.uniqueId,
                  linkedId: conv.linkedId,
                  throughQueue: conv.throughQueue,
                  throughTrunk: calculatedThroughTrunk,
                  direction: conv.direction,
                  connected: conv.connected,
                })
              }
              break
            // @ts-ignore
            case 'busy':
              syncCurrentUserQueueCall(conv)

              // Handle streaming source for outgoing calls
              handleStreamingSource(conv)

              if (
                (uaType === 'mobile' && hasOnlineNethlink()) ||
                (uaType === 'desktop' &&
                  (default_device?.type === 'webrtc' ||
                    (default_device?.type === undefined && !hasOnlineNethlink()) ||
                    (!hasOnlineNethlink() && default_device?.type === 'physical')))
              ) {
                if (conv && conv.connected) {
                  // Current call accepted and update connected call
                  dispatch.currentCall.updateCurrentCall({
                    conversationId: conv.id,
                    displayName: getResolvedDisplayName(conv),
                    number: `${conv.counterpartNum}`,
                    ownerExtension: conv.owner,
                    username:
                      `${
                        extensions &&
                        extensions[conv.counterpartNum] &&
                        extensions[conv.counterpartNum].username
                      }` || '',
                    chDest: conv?.chDest || {},
                    chSource: conv?.chSource || {},
                  })
                  // Update the current call informations for physical devices
                  dispatch.currentCall.checkAcceptedUpdate({
                    acceptedSocket: true,
                  })
                  // Add call to transfer calls
                  dispatch.currentCall.addTransferCalls({
                    type: 'transferred',
                    displayName: getResolvedDisplayName(conv),
                    number: `${conv.counterpartNum}`,
                    startTime: `${getTimestampInSeconds()}`,
                  })

                  // Check if this is a streaming call that was accepted
                  const { isFromStreaming } = store.getState().island
                  if (isFromStreaming && conv.direction === 'out') {
                    // Set view to streamingAnswer for outgoing streaming calls when accepted
                    setTimeout(() => {
                      dispatch.island.setIslandView('streamingAnswer')
                    }, 200)
                  }

                  if (isPhysical()) {
                    checkDefaultDeviceConversationActive(conv)
                  }
                  if (view === 'call' && transferring) {
                    dispatch.currentCall.updateCurrentCall({
                      transferring: false,
                    })
                  }
                }
                // Delete transfer calls if there are more than one ( in case of call switch after transfer)
                if (transferCalls.length > 1) {
                  dispatch.currentCall.deleteTransferCalls()
                }
                // Handle not connected calls
                else if (conv && !conv.connected) {
                  if (transferring && !transferSwitching) {
                    // Handle hangup during transfer
                    const inTransferCalls = transferCalls.find(
                      (item) => item.number === conv.counterpartNum,
                    )
                    if (!conv.connected && inTransferCalls) {
                      // Update transferring data for the current call
                      dispatch.currentCall.updateCurrentCall({
                        transferring: false,
                      })
                      eventDispatch('phone-island-call-transfer-failed', {})
                      // Reset transfer switching
                      // TODO - It needs to enhance how conversation connections (conv.connected) are updated server side
                      // TODO - The transfer end is not handled when the an user hangups or after call switch
                      dispatch.currentCall.updateTransferSwitching(false)
                    }
                  }
                  if (conv?.counterpartName === 'REC') {
                    dispatch.physicalRecorder.setRecordingTempVariable(true)
                  }
                }
                // Handle outgoing call
                if (conv && !conv.connected && conv.direction === 'out') {
                  // Update the current outgoing conversation
                  dispatch.currentCall.checkOutgoingUpdate({
                    outgoingSocket: true,
                    outgoing: conv?.counterpartName === 'REC' ? false : true,
                    displayName: getDisplayName(conv),
                    number: `${conv?.counterpartNum}`,
                    username:
                      `${
                        extensions &&
                        extensions[conv?.counterpartNum] &&
                        extensions[conv?.counterpartNum].username
                      }` || '',
                  })
                }
              }
              break

            case 'onhold':
              syncCurrentUserQueueCall(conv)

              // The new conversation during transferring
              const { counterpartName, counterpartNum, startTime } = conv
              if (
                transferring &&
                counterpartNum &&
                counterpartName &&
                counterpartName !== '<unknown>'
              ) {
                // Add call to transfer calls
                dispatch.currentCall.addTransferCalls({
                  type: 'destination',
                  displayName: getDisplayName(conv),
                  number: counterpartNum,
                  startTime: `${getTimestampInSeconds()}`,
                })
                // Set the current call informations
                dispatch.currentCall.updateCurrentCall({
                  displayName: getDisplayName(conv),
                  number: counterpartNum,
                  startTime: `${startTime / 1000}`,
                  conversationId: conv.id,
                })
                // Set the view of the island to call
                dispatch.island.setIslandView('call')
              }
              break
            case 'busy_ringing':
              eventDispatch('phone-island-call-ringing', {})
              break
            default:
              break
          }
        }
      } else {
        // Without conversation for physical phone management
        if (res.status == 'online' && userTotallyFree()) {
          syncCurrentUserQueueCall(null)

          // Stop ringing sounds
          dispatch.player.stopAudioPlayer()
          // Reset current call info
          dispatch.currentCall.reset()
          dispatch.listen.reset()
          dispatch.physicalRecorder.setRecordingTempVariable(false)
          // Reset isFromStreaming flag
          dispatch.island.setIsFromStreaming(false)
        }
      }
    }

    /**
     * Initialize socket connection and listeners
     */
    const initSocketConnection = () => {
      const currentApiMode = getApiMode(username)

      const socketOptions: any = {
        upgrade: false,
        transports: ['websocket'],
        reconnection: true,
        reconnectionDelay: 2000,
      }

      // Only set path for new API mode
      if (currentApiMode === 'new') {
        socketOptions.path = '/api/ws'
      }

      socket.current = io('https://' + hostName, socketOptions)

      // save websocket to store
      dispatch.websocket.update({ socket: socket.current })

      // Handle socket errors
      socket.current.on('connect', () => {
        console.debug(`Socket connected sid: ${socket.current.id}`)
        eventDispatch('phone-island-socket-connected', {})
      })
      socket.current.on('disconnect', (reason) => {
        console.debug(`Socket disconnect - reason: ${reason}`)
        // Clear the connection check interval on disconnect to avoid stale pings
        if (connectionCheckInterval.current) {
          clearInterval(connectionCheckInterval.current)
          connectionCheckInterval.current = null
        }
        if (reason.includes('server disconnect')) {
          eventDispatch('phone-island-server-disconnected', {})
        } else {
          eventDispatch('phone-island-socket-disconnected', {})
        }
      })
      socket.current.io.on('error', (err) => {
        console.debug(`Socket error: `, err)
      })
      socket.current.on('connect_error', (err) => {
        console.debug(`Socket connect_error: `, err)
      })
      socket.current.io.on('reconnect', (attempt) => {
        // Reset consecutive ping timeout counter on successful reconnection
        consecutivePingTimeouts.current = 0
        eventDispatch('phone-island-socket-reconnected', {})
        console.debug(`Socket reconnect attemp ${attempt} (sid: ${socket.current.id})`)
      })
      socket.current.io.on('reconnect_attempt', (attempt) => {
        console.debug(`Socket reconnect_attempt ${attempt}`)
      })
      socket.current.io.on('reconnect_error', (err) => {
        console.debug(`Socket reconnect_error: `, err)
      })
      socket.current.io.on('reconnect_failed', () => {
        console.debug(`Socket reconnect_failed`)
      })

      // Connection check interval is now started in the authe_ok handler
      // to ensure it only runs after successful authentication

      // Handle connection message
      socket.current.on('connect', () => {
        console.debug('Socket on: ' + hostName + ' is connected!')
        socket.current.emit('login', {
          accessKeyId: `${username}`,
          token: authToken,
          uaType: uaType,
        })
      })

      // Handle authentication success message
      socket.current.on('authe_ok', () => {
        console.debug('Socket authentication success!')
        eventDispatch('phone-island-socket-authorized', {})

        // Start connection check interval after successful authentication
        // Clear any existing interval first to avoid duplicates
        if (connectionCheckInterval.current) {
          clearInterval(connectionCheckInterval.current)
        }
        connectionCheckInterval.current = setInterval(() => {
          socket.current.volatile.emit(
            'ping',
            withTimeout(
              () => {
                // Ping success - reset consecutive timeout counter
                consecutivePingTimeouts.current = 0
                // Remove socket_down alert (async to avoid React error #300 with framer-motion)
                setTimeout(() => {
                  dispatch.alerts.removeAlert('socket_down')
                  eventDispatch('phone-island-alert-removed', {
                    type: 'socket_down',
                  })
                  eventDispatch('phone-island-socket-disconnected-popup-close', {})
                }, 0)
              },
              () => {
                // Ping timeout - increment counter
                consecutivePingTimeouts.current++
                console.debug(
                  `Socket ping timeout (${consecutivePingTimeouts.current}/${STALE_CONNECTION_THRESHOLD}), connected: ${socket.current.connected}`,
                )

                // Set socket_down alert (async to avoid React error #300 with framer-motion)
                setTimeout(() => {
                  // Check for stale connection: socket reports connected but pings keep timing out
                  const isStaleConnection =
                    socket.current.connected &&
                    consecutivePingTimeouts.current >= STALE_CONNECTION_THRESHOLD

                  if (!socket.current.connected || isStaleConnection) {
                    // Check if there's an active call with ICE still connected
                    // If so, skip showing alert - let ICE grace period mechanism handle it
                    const { sipcall }: { sipcall: any } = store.getState().webrtc
                    const { accepted, outgoing } = store.getState().currentCall
                    const iceState = sipcall?.webrtcStuff?.pc?.iceConnectionState
                    const hasActiveCallWithIce =
                      (accepted || outgoing) &&
                      (iceState === 'connected' || iceState === 'completed')

                    if (hasActiveCallWithIce) {
                      console.debug(
                        'Socket unreachable but active call with ICE connected - skipping socket_down alert',
                        {
                          iceState,
                          accepted,
                          outgoing,
                          isStaleConnection,
                          timestamp: new Date().toISOString(),
                        },
                      )
                      return
                    }

                    if (isStaleConnection) {
                      console.warn('Stale socket connection detected - forcing reconnection')
                      // Force disconnect to trigger reconnection
                      socket.current.disconnect()
                    }

                    dispatch.alerts.setAlert('socket_down')
                    eventDispatch('phone-island-socket-disconnected-popup-open', {})
                    console.error('Socket is unreachable!')
                  }
                }, 0)
              },
              5 * 1000,
            ),
          )
        }, 5 * 1000)
      })

      socket.current.on('userMainPresenceUpdate', (res: MainPresenceTypes) => {
        // Update endpoints store
        store.dispatch.users.updateEndpointMainPresence({ ...res.mainPresence })
        // Dispatch dispatchMainPresence Event
        dispatchMainPresence(res)
      })

      socket.current.on('extenHangup', (res: any) => {
        const { endpoints, username } = store.getState().currentUser
        const { isActive, conferenceStartedFrom } = store.getState().conference
        const { view, previewCallFromMobileOrNethlink } = store.getState().island

        // Get user extensions
        const userExtensions = endpoints?.extension || []
        const userExtensionIds = userExtensions.map((ext) => ext.id)
        let summaryExtension = res?.callerNum
        if (
          !userExtensionIds.includes(summaryExtension) &&
          userExtensionIds.includes(res?.channelExten)
        ) {
          summaryExtension = res?.channelExten
        }

        // Find the extension type based on callerNum
        const connectedExtension = userExtensions.find((ext) => ext.id === res.callerNum)

        const extensionType: any = connectedExtension?.type

        const summaryConversationSelection = selectSummaryConversationForHangup(summaryExtension)
        const selectedConversation: any = summaryConversationSelection.conversation

        // Check summary/transcription only for answered calls with a valid uniqueid.
        const linkedId = getConversationLinkedId(selectedConversation)
        const uniqueId = getConversationUniqueId(selectedConversation)
        const conversationWasConnected = selectedConversation?.connected || false

        if (uniqueId && linkedId && conversationWasConnected) {
          eventDispatch('phone-island-summary-call-check', { linkedid: linkedId, uniqueid: uniqueId })
          if (summaryExtension) {
            summaryConversationCache.current.delete(summaryExtension)
          }
        }

        // If cause is normal_clearing and extension is physical or mobile
        // Clean phone-island visibility also after user_busy ( useful for physical devices )
        if (
          (res.cause === 'normal_clearing' &&
            (extensionType === 'physical' || extensionType === 'mobile')) ||
          (res.cause === 'normal_clearing' &&
            (extensionType === 'webrtc' || extensionType === 'nethlink') &&
            previewCallFromMobileOrNethlink) ||
          res?.cause === 'user_busy' ||
          res?.cause === 'not_defined' ||
          res?.cause === 'call_rejected' ||
          res?.cause === 'interworking'
        ) {
          // Reset phone island visibility after 2 seconds to avoid glitches
          setTimeout(() => {
            store.dispatch.island.toggleAvoidToShow(false)
            store.dispatch.island.setPreviewCallFromMobileOrNethlink(false)
          }, 500)
          // Only reset conference if there are no more participants or if user is not in a conference
          if (isActive && conferenceStartedFrom !== username) {
            store.dispatch.conference.resetConference()
          }
        } else if (
          res?.cause === 'normal_circuit_congestion' &&
          isActive &&
          conferenceStartedFrom === username
        ) {
          setTimeout(() => {
            eventDispatch('phone-island-view-changed', { viewType: 'waitingConference' })
          }, 800)
        } else if (
          (res.cause === 'normal_clearing' ||
            res?.cause === 'user_busy' ||
            res?.cause === 'not_defined' ||
            res?.cause === 'call_rejected') &&
          (extensionType === 'webrtc' || extensionType === 'nethlink') &&
          isActive &&
          conferenceStartedFrom !== username
        ) {
          store.dispatch.conference.resetConference()
        }
        // if conference owner and added participant refuses or hangs up with normal_clearing
        if (
          ((res.cause === 'normal_clearing' &&
            (extensionType === 'webrtc' || extensionType === 'nethlink')) ||
            res?.cause === 'call_rejected') &&
          isActive &&
          conferenceStartedFrom === username
        ) {
          const { usersList, pendingUsers } = store.getState().conference
          // Check if there are still participants in the conference (both confirmed and pending)
          const hasConfirmedParticipants = usersList && Object.keys(usersList).length > 0
          const hasPendingParticipants = pendingUsers && Object.keys(pendingUsers).length > 0
          const hasParticipants = hasConfirmedParticipants || hasPendingParticipants

          if (!hasParticipants) {
            store.dispatch.conference.resetConference()
          } else {
            // If there are still participants, keep the waitingConference view
            setTimeout(() => {
              eventDispatch('phone-island-view-changed', { viewType: 'waitingConference' })
            }, 800)
          }
        }
        // if conference owner call the call with the added user inside conference
        if (
          res?.cause === 'interworking' &&
          isActive &&
          conferenceStartedFrom === username &&
          view !== 'waitingConference'
        ) {
          setTimeout(() => {
            eventDispatch('phone-island-view-changed', { viewType: 'waitingConference' })
          }, 800)
        }
        if (res?.cause === 'user_busy') {
          // Get current user's extensions
          const { endpoints, username } = store.getState().currentUser
          const userExtensions = endpoints?.extension || []
          const userExtensionIds = userExtensions.map((ext) => ext.id)

          // Get the current call state to understand if we're the caller or receiver
          const { incoming, accepted, transferring } = store.getState().currentCall

          // Check if there's an active conference
          const { isActive, conferenceStartedFrom } = store.getState().conference

          // When we RECEIVE a call on our extension, callerNum is the busy extension (our own)
          // When we CALL someone, channelExten is one of our extensions (the one we're calling from)
          const isReceivingCall =
            incoming && !!res.callerNum && userExtensionIds.includes(res.callerNum)

          // If we are already on an active call, suppress the busy popup unless it belongs
          // to a conference-owner flow or an attended transfer started by this user.
          const shouldShowOperatorBusy = !isReceivingCall && (!accepted || isActive || transferring)

          // Only show operator busy view if:
          // 1. We are NOT receiving an incoming call to our own extension
          if (shouldShowOperatorBusy) {
            // Set operator busy active with caller information
            store.dispatch.island.setOperatorBusyActive({
              callerNumber: res.callerNum || 'Unknown',
            })

            // Stop busy tone after 4 seconds
            setTimeout(() => {
              store.dispatch.player.stopAudioPlayer()
            }, 4000)

            setTimeout(() => {
              // Play busy tone
              store.dispatch.player.updateStartAudioPlayer({
                src: busyRingtone,
                loop: true,
              })
              store.dispatch.island.setIslandView('operatorBusy')
            }, 600)

            // If conference is active and we're the owner, return to conference after timeout
            if (isActive && conferenceStartedFrom === username) {
              setTimeout(() => {
                eventDispatch('phone-island-view-changed', { viewType: 'waitingConference' })
              }, 4000)
            }
          }
        }

        // Handle subscriber_absent - when added participant rejects the call
        if (res?.cause === 'subscriber_absent') {
          const { isActive, conferenceStartedFrom } = store.getState().conference
          const { username } = store.getState().currentUser

          // Only handle if conference is active and current user is the owner
          if (isActive && conferenceStartedFrom === username) {
            // Check if there are still participants in the conference (both confirmed and pending)
            const { usersList, pendingUsers } = store.getState().conference
            const hasConfirmedParticipants = usersList && Object.keys(usersList).length > 0
            const hasPendingParticipants = pendingUsers && Object.keys(pendingUsers).length > 0
            const hasParticipants = hasConfirmedParticipants || hasPendingParticipants

            if (hasParticipants) {
              // Return to waiting conference view to manage other participants
              setTimeout(() => {
                eventDispatch('phone-island-view-changed', { viewType: 'waitingConference' })
              }, 800)
              // Remove from pending users if exists
              if (pendingUsers && pendingUsers[res.callerNum]) {
                store.dispatch.conference.removePendingUser(res.callerNum)
              }
            } else {
              // No participants left, reset conference
              store.dispatch.conference.resetConference()
            }
          }
        }
      })

      // Avoid to show phone island if call is connected with other extension
      socket.current.on('extenConnected', (res: { extenConnected: string }) => {
        // Get the current user's extensions

        const { default_device, endpoints } = store.getState().currentUser
        const userExtensions = endpoints?.extension || []

        // Find the extension type
        const connectedExtension = userExtensions.find((ext) => ext.id === res.extenConnected)
        const extensionType: any = connectedExtension?.type

        // Reset only if the extension type is not webrtc or nethlink
        // ( avoid to not show phone island if default is physical and extensionType is physical)
        if (
          ((default_device?.type === 'webrtc' || default_device?.type === 'nethlink') &&
            extensionType &&
            (extensionType === 'mobile' || extensionType === 'physical')) ||
          (default_device?.type === 'physical' && extensionType && extensionType !== 'physical')
        ) {
          // Avoid to show phone island in case of answer from physical or mobile device
          store.dispatch.island.toggleAvoidToShow(true)
          // Set the preview call flag
          store.dispatch.island.setPreviewCallFromMobileOrNethlink(true)
          // Launch an event to advert the user that the call it's answered from another device
          eventDispatch('phone-island-call-answered', { extensionType })
        }
      })

      socket.current.on('extenUpdate', (res: ExtensionTypes) => {
        // Update extensions and conversations in users store
        dispatch.users.updateExtension(res)

        //retrieve all extensions from store
        const { extensions }: any = store.getState().users
        const deviceMap: any = {}

        const userInformation = store.getState().currentUser
        // Create a map of extensions for each user
        for (const key in extensions) {
          const user: any = extensions[key].username
          const ext: any = extensions[key].exten

          if (!deviceMap[user]) {
            deviceMap[user] = []
          }

          deviceMap[user].push(ext)
        }

        const associatedExtensions: any = deviceMap[res.username]
        const conversationKeys = Object.keys(res.conversations || {})
        const isCurrentUserUpdate = res.username === username

        // Initialize conversation
        let conv = res.conversations?.[conversationKeys[0]] || {}
        if (isCurrentUserUpdate) {
          cacheSummaryConversations(res.exten, res.conversations)
        }

        // Check if this is a mobile extension call for the current user
        let isMobileExtensionCall = false
        if (res?.username === username && !isEmpty(conv) && conv?.owner) {
          const matchingExtension = userInformation?.endpoints?.extension?.find(
            (ext: any) => ext.id === conv.owner,
          )
          if (matchingExtension && matchingExtension.type === 'mobile') {
            isMobileExtensionCall = true
          }
        }

        // Update all extensions and send the dispatch event
        dispatchExtensions(res)

        // second step update conversation

        // Check if conversation is empty
        if (isEmpty(conv)) {
          // Check if there is at least one conversation not empty
          const hasNonEmptyConversation = associatedExtensions?.some((ext: any) => {
            const extConversations = extensions[ext]?.conversations

            if (!isEmpty(extConversations)) {
              // not empty conversation found
              return true
            }

            return false
          })

          if (!hasNonEmptyConversation) {
            // Conversation is empty and there is no conversation for the user
            dispatchConversations(res)
          }
        } else if (!isMobileExtensionCall) {
          // Dispatch conversation event
          dispatchConversations(res)
        }

        if (isMobileExtensionCall && res?.status === 'busy' && res?.username === username) {
          store.dispatch.island.toggleAvoidToShow(true)
          store.dispatch.island.setPreviewCallFromMobileOrNethlink(true)
        }
        // Handle only the events of the user
        if (res.username === username) {
          handleCurrentUserEvents(res, conv)
          // Update the conversations of the user
          dispatch.currentUser.updateConversations(res)
        }
      })

      // `queueUpdate` is the socket event when the data of a queue updates
      socket.current.on('queueUpdate', (res: QueuesUpdateTypes) => {
        dispatch.queue.updateQueue(res)

        const activeConversation = getActiveConversationForCurrentCall()

        if (activeConversation) {
          syncCurrentUserQueueCall(activeConversation)
        }

        // Dispatch queueUpdate event
        dispatchQueueUpdate(res)
      })

      // `queueMemberUpdate` is the socket event when the data of a queue member changes
      socket.current.on('queueMemberUpdate', (res: QueueUpdateMemberTypes) => {
        dispatch.queue.updateQueueMember(res)
        // Dispatch queueMemberUpdate event
        dispatchQueueMemberUpdate(res)
      })

      // `takeOver` is the socket event when the user does login from another new window
      socket.current.on('takeOver', () => {
        // Dispatch takeOver event
        dispatchAlreadyLogin()
      })

      // `serverReload` is the socket event when server is reloaded
      socket.current.on('serverReloaded', () => {
        // Dispatch serverReload event
        dispatchServerReload()
      })

      // `parkingUpdate` is the socket event when a call is parked or unparked
      socket.current.on('parkingUpdate', (event) => {
        // Dispatch parking update event with the parking information
        dispatchParkingUpdate(event)
      })

      // `actionNethLink` is the socket event when user make a call or a action from NethLink and has a physical device
      socket.current.on('actionNethLink', (link, urlType) => {
        // Dispatch phone island physical call event with the link and the urlType
        dispatchUrlCall(link, urlType)
      })

      // `satellite/summary` is the socket event when summary is ready
      socket.current.on('satellite/summary', (data: any) => {
        const uniqueid = data?.uniqueid || data?.uniqueId
        const linkedid = data?.linkedid || data?.linkedId

        if (uniqueid) {
          dispatchSummaryReady({
            linkedid,
            uniqueid,
            display_name: data?.display_name,
            display_number: data?.display_number,
            source: 'socket',
          })
        }
      })

      socket.current.on('message', (data: any) => {
        switch (data.message) {
          case 'videoCallStart':
            {
              const {
                currentCall: { incoming, outgoing, accepted },
                currentUser: { default_device },
                island: { isFromStreaming, view },
              } = store.getState()

              const isWebrtcDevice =
                default_device?.type === 'webrtc' || default_device?.type === 'nethlink'
              const hasCompatibleCallState = incoming || outgoing || accepted
              const canOpenVideoView =
                isWebrtcDevice && hasCompatibleCallState && !isFromStreaming && view !== 'video'

              if (canOpenVideoView) {
                dispatch.island.toggleSideViewVisible(false)
                dispatch.island.toggleTranscriptionViewVisible(false)
                dispatch.island.setIslandView('video')
              }
            }

            dispatchVideoCallStarted({
              initiator: 'remote',
              callUser: (data as VideoCallMessage).callUser,
              destUser: (data as VideoCallMessage).destUser,
            })
            break
          case 'screenSharingStart':
            dispatch.island.toggleSideViewVisible(false)
            dispatch.island.toggleTranscriptionViewVisible(false)
            dispatch.island.setIslandView('video')

            dispatch.screenShare.update({
              isJoiningScreenShare: true,
              room: (data as ScreenSharingMessage).roomId,
            })
            break
          case 'screenSharingStop':
            dispatch.island.toggleSideViewVisible(false)
            dispatch.island.toggleTranscriptionViewVisible(false)
            dispatch.island.setIslandView('video')

            dispatch.screenShare.update({
              isLeavingScreenShare: true,
            })
            break
          default:
            console.warn('Socket: unknown message type ', data.message)
        }
      })

      // `updateDefaultDevice` is the socket event when user change the default device
      socket.current.on('updateDefaultDevice', (extension: string) => {
        // Dispatch phone island physical call event with the link and the urlType
        dispatchDefaultDeviceUpdate(extension)
        // Update the internal store
        const { extensions } = store.getState().users
        const { endpoints } = store.getState().currentUser
        if (!extensions || !endpoints) return

        const extensionInformations: any = Object.values(extensions).filter(
          (ext) => ext?.exten === extension,
        )
        if (extensionInformations.length === 0) return

        let objectComplete = extensionInformations[0]
        const endpointExtension = endpoints.extension.find(
          (endpoint) => endpoint.id === objectComplete.exten,
        )
        if (endpointExtension) {
          objectComplete = { ...objectComplete, type: endpointExtension.type }
        }

        store.dispatch.currentUser.updateCurrentDefaultDevice(objectComplete)
        //make sure to check the media permissions
        checkMediaPermissions()
      })

      socket.current.on('confBridgeUpdate', (res: any) => {
        if (res && res?.users) {
          // Get User informations
          const conferenceId = res?.id
          const conferenceUsers = res?.users

          // Get current users list to preserve mute status
          const { usersList } = store.getState().conference

          // Create a copy of the new conference users while preserving mute status
          const updatedConferenceUsers = { ...conferenceUsers }

          // Preserve mute status for existing users
          if (usersList) {
            Object.keys(updatedConferenceUsers).forEach((userId) => {
              if (usersList[userId]) {
                // Keep the existing mute status instead of using the server's value
                updatedConferenceUsers[userId] = {
                  ...updatedConferenceUsers[userId],
                  muted: usersList[userId].muted,
                }
              }
            })
          }

          store.dispatch.conference.updateConferenceUsersList(updatedConferenceUsers)
          store.dispatch.conference.updateConferenceId(conferenceId)
        }
      })

      socket.current.on('confBridgeEnd', (res: any) => {
        if (res && res?.id) {
          // Reset the conference store when conference ends
          store.dispatch.conference.resetConference()
          eventDispatch('phone-island-conference-finished', {})
        }
      })

      socket.current.on('callWebrtc', (res: any) => {
        // On call event from socket dispatch the call start event
        eventDispatch('phone-island-call-start', { number: res })
      })

      socket.current.on('newVoiceMessageCounter', (res: any) => {
        eventDispatch('phone-island-voicemail-received', { voicemailInfo: res })
      })

      socket.current.on('streamingSourceUpdate', (res: any) => {
        eventDispatch('phone-island-streaming-information-received', { res })
        const streamingData = res.streaming || (res.res && res.res.streaming)

        if (streamingData) {
          const { source, image } = streamingData
          if (source && image) {
            const { isFromStreaming } = store.getState().island
            const { streamingSourceNumber } = store.getState().currentCall
            const sourceId = getStreamingSourceId(streamingSourceNumber)

            dispatch.streaming.updateSourceImage({
              source: source,
              image: image,
            })
          }
        }
      })

      // Handle satellite/transcription messages
      socket.current.on('satellite/transcription', (transcriptionData: any) => {
        // Drop chunks belonging to a different call than the one open in the
        // transcription view, to prevent cross-call transcript mixing when more
        // than one call is being transcribed at the same time.
        //
        // The realtime chunk identifies its call in the `uniqueid` field, which
        // actually carries the call_id (linkedid when available — see satellite
        // backend). We only filter when we know the active linkedid: in that case
        // matching is reliable. If we don't, we forward everything (unchanged
        // behaviour) to avoid ever dropping a valid chunk.
        const active = activeTranscriptionIdsRef.current
        const chunkId = transcriptionData?.uniqueid
        const chunkLinkedId = transcriptionData?.linkedid
        if (active.linkedid && chunkId) {
          const matchesActive = (id: any) =>
            id != null && (id === active.linkedid || id === active.uniqueid)
          if (!matchesActive(chunkId) && !matchesActive(chunkLinkedId)) {
            return
          }
        }
        // Dispatch the transcription event to external listeners
        eventDispatch('phone-island-conversation-transcription', transcriptionData)
      })
    }

    initSocketConnection()

    // Stop the check socket interval
    // Close the socket connection
    return () => {
      clearInterval(connectionCheckInterval.current)
      // Drop fallback-resolved caller names so a previous user's group-shared
      // phonebook names cannot surface after a host/user/auth change (the effect
      // re-runs on username/authToken changes without unmounting the component).
      resolvedContactNamesRef.current.clear()
      contactLookupConversationsRef.current.clear()
      socket.current.close()
    }
  }, [hostName, username, authToken, uaType, dispatch])

  // Manage reload events
  useEffect(() => {
    if (reload) {
      console.info('Socket reload requested')
      const { data } = store.getState().alerts
      const { forceReload } = store.getState().island

      // Check if socket is actually down using alerts (more reliable than socket.connected)
      const isSocketDown = data.socket_down?.active || false

      // Only reconnect if socket_down alert is active OR force reload is requested
      if (isSocketDown || forceReload) {
        console.info(
          forceReload
            ? 'Force reload requested, performing Socket reconnection'
            : 'Socket down detected (alert active), performing reconnection',
        )
        // Reset force reload flag
        if (forceReload) {
          store.dispatch.island.setForceReload(false)
        }
        // Clear the connection check interval to avoid stale ping timeouts during reconnection
        if (connectionCheckInterval.current) {
          clearInterval(connectionCheckInterval.current)
          connectionCheckInterval.current = null
        }
        // Disconnect and reconnect socket
        setTimeout(() => {
          socket.current.disconnect()
          socket.current.connect()
          // Execute the reloaded callback
          reloadedCallback()
        }, 100)
      } else {
        console.info('Socket already connected (no alert active), skipping reconnection')
        // Execute callback without reload
        reloadedCallback()
      }
    }
  }, [reload])

  return <>{children}</>
}
